import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Input,
  InputAdornment,
  IconButton,
  MenuItem,
  Collapse,
  TextField,
  FormHelperText,
} from "@mui/material";
import { Search, Cancel } from "@mui/icons-material";
import "./orderInvoice.scss";
import {
  InvoiceHeadType,
  InvoiceItemType,
  InvoiceWizardProps,
} from "../../shared/models/invoice";
import {
  cleanupResizeObserver,
  handleResizeObserverError,
} from "../../shared/utils/resizeObserverUtils";
import { fetchOrgPartyDetailsApi } from "../../store/api/party";
import store from "../../store/store";
import NavigateBackButton from "../../shared/components/customNavigateButton/customNavigateButton";
import { MESSAGE_TYPES, PAGES_URL, UOM_TYPE } from "../../shared/constants/constants";
import PageMessage from "../pageMessage/pageMessage";
import InvoiceDocument from "../invoice/invoiceDocument/invoiceDocument";
import InvoiceVerificationForm from "../invoice/invoiceVerificationForm/invoiceVerificationForm";
import ErrorPopup from "../dialogBox/errorModal";
import SuccessDialog from "../dialogBox/successDialog";
import TermsConditionsBar from "../pageBars/termsConditionsBar/termsConditionsBar";
import OrderInvoiceVerificationForm from "./orderInvoiceVerificationForm";
import { getUOMByType } from "../../store/api/common";
import { roundOff } from "../../shared/utils/utils";

const OrderInvoiceWizard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const stateInvoiceObject = location.state?.invoiceObject || {};
  const stateOrderId = location.state?.orderId || "";
  const stateOrderItems = location.state?.orderItems || {};
  const stateContentDetails = location.state?.contentDetails || {};
  const wizardRef = useRef<HTMLDivElement>(null);

  // State
  const [invoiceHeadData, setInvoiceHeadData] = useState<InvoiceHeadType>({
    invoiceName: "",
    description: "",
    customPONumber: "",
    referenceDate: null,
    expectedDeliveryDate: null,
    fulfilledByPartyId: "",
    paymentTerms: "",
    termsNConditions: "",
    adjustments: [],
    hsnAck: false,
    grandTotal: 0,
    totalTaxAmount: 0,
    totalAmount: 0,
    invoiceContactMechs: [],
    items: [],
    totalAdjustedAmount: 0,
    totalAmountWithoutAdjustment: 0,
    externalRoundedOff: 0,
    discount: {
      amount: 0,
      tax: 0
    }
  });

  // Additional state for step 2
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [products, setProducts] = useState<any[]>([]);
  const [ITEM_UOMs, setITEM_UOMs] = useState<any[]>([]);
  const [adjustments, setAdjustments] = useState<any[]>([]);

  // Dialog states
  const [error, setError] = useState({ open: false, message: "" });
  const [success, setSuccess] = useState({ open: false, message: "" });
  const [openHsn, setOpenHsn] = useState(false);

  // Seller selection states
  const [fileUploaded, setFileUploaded] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(false);

  // Handle ResizeObserver errors
  useEffect(() => {
    const cleanup = handleResizeObserverError();

    return () => {
      cleanup();
      cleanupResizeObserver(wizardRef.current);
    };
  }, []);

  // Fetch seller relations
  useEffect(() => {
    store.dispatch(getUOMByType(UOM_TYPE.QUANTITY_MEASURE)).unwrap().then((res) => {
      if (res.status === 200) {
        setITEM_UOMs(res.data)
      }
    })
  }, []);

  // Handlers
  const handleCloseError = () => {
    setError({ open: false, message: "" });
  };

  useEffect(() => {
    handleUploadedInvoiceData(stateInvoiceObject);
  }, []);

  const handleUploadedInvoiceData = (data: any) => {
    console.log("Invoice data uploaded:", data);

    // Parse the invoice_data string if it's a string
    let parsedData = data;
    if (typeof data === "string") {
      try {
        parsedData = JSON.parse(data);
      } catch (error) {
        console.error("Error parsing invoice data:", error);
        setError({ open: true, message: "Error parsing invoice data" });
        return;
      }
    }

    // Helper function to safely parse dates
    const safeParseDate = (dateString: string): Date | null => {
      if (!dateString) return null;
      try {
        // Handle DD/MM/YYYY format (common in Indian invoices)
        if (dateString.includes("/")) {
          const parts = dateString.split("/");
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
            const year = parseInt(parts[2], 10);

            if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
              const date = new Date(year, month, day);
              return isNaN(date.getTime()) ? null : date;
            }
          }
        }

        // Fallback to standard Date parsing
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? null : date;
      } catch (error) {
        return null;
      }
    };

    let tempSubTotal = 0;
    let tempTaxTotal = 0;
    let tempGrandTotal = 0;

    // Transform the uploaded data to match our expected format
    let transformedData: any = {
      invoiceName: parsedData?.invoiceName || "",
      customPONumber: parsedData?.invoiceNumber || "",
      referenceDate: safeParseDate(parsedData?.invoiceDate),
      expectedDeliveryDate: safeParseDate(parsedData?.invoiceDate), // Use invoice_date as fallback for created date
      paymentTerms: parsedData.paymentTerms || "",
      // totalAmount: parseFloat(parsedData?.totalAmount || 0),
      // totalTaxAmount: parseFloat(parsedData?.totalTaxAmount || 0),
      // grandTotal: parseFloat(parsedData?.grandTotal || 0),
      // Add the extra data as requested
      items:
        parsedData.invoiceItems?.map((item: any) => {
          const tempItemAmount = roundOff(
            parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0)
          );
          const tempItemTax = roundOff(
            (tempItemAmount * parseFloat(item.gst || 0)) / 100
          );
          const tempItemTotal = roundOff(tempItemAmount + tempItemTax);
          tempSubTotal += tempItemAmount;
          tempTaxTotal += tempItemTax;
          tempGrandTotal += tempItemTotal;
          return {
            item: item.item || "",
            itemSeqId: item.itemSeqId,
            brandName: item.brandName || "",
            size: item.size || "",
            hsnCode: item.hsnCode || "",
            quantityUOM: item.quanityUOM || "",
            quantity: item.quantity || "",
            pricePerUnit: parseFloat(item.unitPrice || 0),
            gst: parseFloat(item.gst || 0),
            amount: tempItemAmount || parseFloat(item.amount || 0),
            itemTypeId: item.invoiceItemTypeId || "",
            description: "",
            finalUnitPrice: parseFloat(item.finalUnitPrice || 0),
            taxableAmount:
              tempItemAmount || parseFloat(item.taxableAmount || 0),
            taxAmount: tempItemTax || parseFloat(item.taxAmount || 0), // Will be calculated
            content: [],
            comment: "",
            productId: "",
            orderItemSeqId: item.orderItemSeqId,
          };
        }) || [],
      invoiceContactMechs: [
        {
          purposeType: "BILLING_ADDRESS",
          address1: parsedData.billingAddress?.address1 || "",
          city: parsedData.billingAddress?.city || "",
          state: parsedData.billingAddress?.state || "",
          postalCode: parsedData.billingAddress?.pincode || "",
          partyName: parsedData.billingAddress?.name || "",
          gstin: parsedData.billTo?.gstin || "",
        },
        {
          purposeType: "SHIPPING_ADDRESS",
          address1: parsedData.shippingAddress?.address1 || "",
          city: parsedData.shippingAddress?.city || "",
          state: parsedData.shippingAddress?.state || "",
          postalCode: parsedData.shippingAddress?.pincode || "",
          partyName: parsedData.shippingAddress?.name || "",
          gstin: parsedData.shippingAddress?.gstin || "",
        },
      ],
    };
    // Updating totals after calculating item-wise totals
    transformedData = {
      ...transformedData,
      totalAmount: roundOff(tempSubTotal),
      totalTaxAmount: roundOff(tempTaxTotal),
      grandTotal: roundOff(tempGrandTotal),
    };

    setAdjustments(
      parsedData?.adjustments?.map((parsedAdjustment: any) => {
        const tempAdjustmentTax = roundOff(Math.abs((parsedAdjustment.adjustmentAmount * parsedAdjustment.taxPercentage)/100))
        return {
          description: parsedAdjustment.description || "Discount",
          adjustmentAmount: Math.abs(parsedAdjustment.adjustmentAmount) || 0,
          taxPercentage: parsedAdjustment.taxPercentage || 0,
          adjustmentTax: Math.abs(parsedAdjustment.adjustmentTax) || tempAdjustmentTax || 0,
          adjustmentTotal:
            Math.abs(parsedAdjustment.adjustmentTotal) ||
            (
              Math.abs(Number(parsedAdjustment.adjustmentAmount || 0)) +
              Math.abs(Number(parsedAdjustment.adjustmentTax || tempAdjustmentTax || 0))
            ).toFixed(2) ||
            0,
          adjustmentType:
            parsedAdjustment.adjustmentType || 0 || "DISCOUNT_ADJUSTMENT",
        };
      })
    );

    setInvoiceHeadData((prev) => ({
      ...prev,
      ...transformedData,
    }));

    // Mark file as uploaded and processed
    setFileUploaded(true);
  };

  const handleAddInvoiceRow = (orderItem: any = {}) => {
    const newItem: InvoiceItemType = {
      item: orderItem?.item || "",
      orderItemSeqId: orderItem?.itemSeqId,
      itemTypeId: orderItem?.itemTypeId || "",
      brandName: orderItem?.brandName || "",
      size: orderItem?.size || "",
      description: orderItem?.description || "",
      quantityUOM: orderItem?.quanityUOM || "",
      quantity: orderItem?.quantity || 0,
      pricePerUnit: orderItem?.finalUnitPrice || 0,
      finalUnitPrice: orderItem?.finalUnitPrice || 0,
      taxableAmount: orderItem?.taxableAmount || 0,
      gst: orderItem?.gst || 0,
      taxAmount: orderItem?.taxAmount || 0,
      amount: orderItem?.amount || 0,
      content: [],
      comment: orderItem?.item || "",
      productId: orderItem?.productId || "",
      hsnCode: orderItem?.hsnCode || "",
    };
    const totalAmount = parseFloat(newItem.taxableAmount.toFixed(2));
    const totalTaxAmount = parseFloat(newItem.taxAmount.toFixed(2));
    const grandTotal = parseFloat(newItem.amount.toFixed(2));

    setInvoiceHeadData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
      totalAmount: parseFloat((prev.totalAmount + totalAmount).toFixed(2)),
      totalTaxAmount: parseFloat(
        (prev.totalTaxAmount + totalTaxAmount).toFixed(2)
      ),
      grandTotal: parseFloat((prev.grandTotal + grandTotal).toFixed(2)),
    }));
  };

  const resetValidationErrors = (errorName: string) => {
    setValidationErrors((prev: any) => {
      const newErrors = { ...prev };
      delete newErrors[errorName];
      return newErrors;
    });
  };

  const handleDataChange = (field: string, value: any) => {
    setInvoiceHeadData((prev) => {
      const newData = { ...prev };

      // Handle nested field updates
      if (field.includes(".")) {
        const [parent, child] = field.split(".");
        if (parent === "seller") {
          newData.seller = { ...newData.seller, [child]: value } as any;
        } else if (parent === "billingAddress") {
          const billingIndex = newData.invoiceContactMechs?.findIndex(
            (contact: any) => contact.purposeType === "BILLING_ADDRESS"
          );
          if (billingIndex !== -1) {
            newData.invoiceContactMechs[billingIndex] = {
              ...newData.invoiceContactMechs[billingIndex],
              [child]: value,
            };
          }
        } else if (parent === "shippingAddress") {
          const shippingIndex = newData.invoiceContactMechs?.findIndex(
            (contact: any) => contact.purposeType === "SHIPPING_ADDRESS"
          );
          if (shippingIndex !== -1) {
            newData.invoiceContactMechs[shippingIndex] = {
              ...newData.invoiceContactMechs[shippingIndex],
              [child]: value,
            };
          }
        }
      } else {
        // Type-safe field assignment
        (newData as any)[field] = value;
      }

      return newData;
    });
  };

  return (
    <Box className="invoice-wizard" ref={wizardRef}>
      <NavigateBackButton
        label="Back to Homepage"
        navigateLink={`/${PAGES_URL.ACCOUNT_MGR}/${PAGES_URL.INVOICE}`}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          justifyContent: "space-between",
          alignContent: "center",
          my: 2,
        }}
      >
        <Typography variant="h4" fontWeight={"bold"}>
          Upload Invoice
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: "#fff",
          my: 1,
          boxShadow: 3,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Collapse in={true}>
          <Box className="step-content">
            <OrderInvoiceVerificationForm
              invoiceData={invoiceHeadData}
              onDataChange={handleDataChange}
              invoiceHeaderData={invoiceHeadData}
              setInvoiceHeadData={setInvoiceHeadData}
              handleAddInvoiceRow={handleAddInvoiceRow}
              ITEM_UOMs={ITEM_UOMs}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              resetValidationErrors={resetValidationErrors}
              setError={setError}
              products={products}
              adjustments={adjustments}
              setAdjustments={setAdjustments}
              onInvoiceCreated={(value) => {
                setInvoiceCreated(value);
                if (!value) {
                  // Reset seller selection and file upload state
                  setFileUploaded(false);
                }
              }}
              orderId={stateOrderId}
              orderItems={stateOrderItems}
              contentDetails={stateContentDetails}
              setSuccess={setSuccess}
            />
          </Box>
        </Collapse>
      </Box>

      {/* Error Dialog */}
      <ErrorPopup
        open={error.open}
        message={error.message}
        onClose={handleCloseError}
      />

      {/* Success Dialog */}
      {success.open && (
        <SuccessDialog
          open={success.open}
          message={success.message}
          onClose={() => setSuccess({ open: false, message: "" })}
        />
      )}

      {/* Terms Conditions Bar */}
      <TermsConditionsBar
        messageType="HSN_AI"
        open={openHsn}
        setOpen={setOpenHsn}
        handleNext={() => {}}
        handleCancel={() => {}}
      />
    </Box>
  );
};

export default OrderInvoiceWizard;
